import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiHome, FiSearch, FiGrid, FiSettings, FiDatabase, FiLayers, FiTool, FiCircle, FiPhone, FiMail } = FiIcons;

const Sidebar = () => {
  const location = useLocation();
  const [showSupportModal, setShowSupportModal] = useState(false);

  const menuItems = [
    { path: '/', label: 'Übersicht', icon: FiHome },
    { path: '/search', label: 'Erweiterte Suche', icon: FiSearch },
    { path: '/categories', label: 'Kategorien', icon: FiGrid },
    { path: '/bearings', label: 'Wälzlager', icon: FiCircle },
    { path: '/seals', label: 'Dichtungstechnik', icon: FiLayers },
    { path: '/linear', label: 'Lineartechnik', icon: FiTool },
    { path: '/technical-docs', label: 'Technische Daten', icon: FiDatabase },
    { path: '/settings', label: 'Einstellungen', icon: FiSettings }
  ];

  const handleSupportClick = () => {
    setShowSupportModal(true);
  };

  return (
    <>
      <motion.aside
        className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 bg-white border-r border-gray-200 shadow-sm flex flex-col"
        initial={{ x: -300 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {menuItems.map((item, index) => (
              <motion.li
                key={item.path}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Link
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    location.pathname === item.path
                      ? 'bg-red-50 text-red-700 border-r-4 border-red-600'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-black'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              </motion.li>
            ))}
          </ul>
        </nav>

        {/* Technical Support - Fixed at bottom */}
        <div className="p-4 border-t border-gray-200">
          <div className="bg-gradient-to-r from-gray-50 to-white p-4 rounded-lg border border-red-100 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-2">Technischer Support</h3>
            <p className="text-sm text-gray-600 mb-3">
              Benötigen Sie Hilfe bei der Produktauswahl oder technischen Spezifikationen?
            </p>
            <button 
              onClick={handleSupportClick}
              className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium shadow-sm"
            >
              Kontakt aufnehmen
            </button>
          </div>
        </div>
      </motion.aside>

      {/* Support Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Technischer Support</h3>
              <button
                onClick={() => setShowSupportModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Unser Expertenteam steht Ihnen für technische Fragen zur Verfügung:
              </p>
              
              <div className="space-y-3">
                <a
                  href="tel:+49-123-456789"
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-red-200 hover:bg-red-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiPhone} className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Telefon</p>
                    <p className="text-sm text-gray-600">+49 (0) 123 456789</p>
                  </div>
                </a>
                
                <a
                  href="mailto:support@zeak.de"
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-red-200 hover:bg-red-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiMail} className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">E-Mail</p>
                    <p className="text-sm text-gray-600">support@zeak.de</p>
                  </div>
                </a>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Servicezeiten:</strong><br />
                  Mo-Fr: 08:00 - 17:00 Uhr<br />
                  Durchschnittliche Antwortzeit: &lt; 2 Stunden
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowSupportModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Schließen
              </button>
              <a
                href="mailto:support@zeak.de"
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-center"
                onClick={() => setShowSupportModal(false)}
              >
                E-Mail senden
              </a>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default Sidebar;